import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {UserMin} from "platformed-client/types";
import {q, useQueryData} from "../state";
import {ProductContext} from "../Products/SharedPages/ProductContext";
import {Box, HStack, Icon, Input, Stack, Text} from "@chakra-ui/react";
import {withSuspense} from "../state/withSuspense";
import {UserPlusIcon} from "@heroicons/react/20/solid";
import {useIsAdmin} from "../hooks/roles";
import {Avatar} from "./ui/avatar";
import T from "platformed-components/i18n/T";
import useTranslate from "platformed-components/i18n/useTranslate";

export type UserPickerValue = {type: "User"; user: UserMin} | {type: "Invite"; nameOrEmail: string};

function valueEq(a?: UserPickerValue | null, b?: UserPickerValue | null): boolean {
  if (a && b) {
    if (a.type === "User" && b.type === "User") {
      return a.user.user_id === b.user.user_id;
    } else if (a.type === "Invite" && b.type === "Invite") {
      return a.nameOrEmail === b.nameOrEmail;
    } else {
      return false;
    }
  } else {
    return !a && !b;
  }
}

export const UserPicker = withSuspense(
  ({
    value,
    onSelect,
    autoFocus,
    onSubmit,
    allUsers,
  }: {
    value?: UserPickerValue;
    onSelect: (user: UserPickerValue | undefined) => void;
    autoFocus?: boolean;
    onSubmit?: () => void;
    // List every account user, ignoring the ambient product. Set this on
    // access-granting pickers (role/team membership), whose purpose is to add
    // people who don't yet have access to the current product — without it they
    // can never be re-added once their access is revoked.
    allUsers?: boolean;
  }) => {
    const t = useTranslate();
    const [name, setName] = useState(
      (value?.type === "User" ? (value?.user.name ?? value?.user.primary_email) : value?.nameOrEmail) ?? "",
    );
    const product = useContext(ProductContext);
    const users = useQueryData(q.accountUsers(allUsers ? "All" : product));
    // Inviting a brand-new user is admin-only (it also assigns their access), so
    // only admins are offered the "Invite" option.
    const isAdmin = useIsAdmin();
    const items: UserPickerValue[] = useMemo(() => {
      const lowerName = name.toLowerCase();
      const filteredUsers = users
        .map(({user}) => user)
        .filter(
          user => user.name?.toLowerCase().includes(lowerName) || user.primary_email?.toLowerCase().includes(lowerName),
        )
        .sort((a, b) => {
          // Given that the name is a match, a shorter name is a better match, so sort shorter names first, and
          // then alphabetically.
          const aName = a.name ?? "";
          const bName = b.name ?? "";
          const aScore = aName.length / (lowerName.length + 1);
          const bScore = bName.length / (lowerName.length + 1);
          const score = bScore - aScore;
          if (score === 0) {
            return aName.localeCompare(bName);
          } else {
            return score;
          }
        });
      const extraItems: UserPickerValue[] =
        !isAdmin ||
        filteredUsers.some(
          user => user.name?.toLowerCase() === lowerName || user.primary_email?.toLowerCase() === lowerName,
        )
          ? []
          : [{type: "Invite", nameOrEmail: name}];
      return [...extraItems, ...filteredUsers.map(user => ({type: "User", user}) as const)];
    }, [users, name, isAdmin]);

    const selectedItem = items.find(item => valueEq(item, value));

    useEffect(() => {
      if (!valueEq(selectedItem, value)) {
        // Inviting is admin-only, so non-admins fall back to "no selection"
        // rather than a phantom Invite value — otherwise consumers would offer
        // an "Invite and …" action that 403s.
        onSelect(selectedItem ?? (isAdmin ? {type: "Invite", nameOrEmail: name} : undefined));
      }
      if (!selectedItem && (items.length === 1 || (items.length === 2 && items[0].type === "Invite"))) {
        onSelect(items[items.length - 1]);
      }
    }, [items, selectedItem, value, onSelect, name, isAdmin]);

    const keyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && selectedItem && onSubmit) {
          e.preventDefault();
          onSubmit();
        }
      },
      [onSubmit, selectedItem],
    );

    return (
      <Stack gap={0}>
        <Input
          placeholder={t("c.Search[ellipsis]")}
          value={name}
          onChange={e => setName(e.target.value)}
          borderBottomRadius={items.length ? 0 : undefined}
          autoFocus={autoFocus}
          onKeyDown={keyDown}
          borderBottom={items.length ? "none" : undefined}
        />
        {items.length > 0 && (
          <Stack
            overflowY="auto"
            maxH="200px"
            gap={0}
            borderColor="gray.200"
            borderWidth="1px"
            role="listbox"
            roundedBottom="md"
          >
            {items.map(item => {
              const key = item.type === "User" ? item.user.user_id : item.nameOrEmail;
              const selected = valueEq(item, selectedItem);
              return (
                <Box
                  key={key}
                  _hover={selected ? {} : {bg: "blue.50"}}
                  bg={selected ? "blue.500" : "white"}
                  fontWeight={selected ? "semibold" : "normal"}
                  color={selected ? "white" : "gray.700"}
                  onClick={() => onSelect(item)}
                  p={2}
                  cursor="default"
                  role="option"
                >
                  {item.type === "User" ? (
                    <HStack>
                      <Avatar
                        src={item.user.avatar_url ?? undefined}
                        name={item.user.name ?? item.user.primary_email ?? undefined}
                        size="2xs"
                      />
                      <Text>{item.user.name ?? item.user.primary_email}</Text>
                    </HStack>
                  ) : (
                    <HStack>
                      <Stack bg="green.500" color="white" rounded="full" w={6} h={6} justify="center" align="center">
                        <Icon as={UserPlusIcon} boxSize="1em" />
                      </Stack>
                      <Text>
                        {item.nameOrEmail ? (
                          <T
                            keyName="c.Invite_user_to_account"
                            params={{
                              name: (
                                <Text as="span" fontWeight="semibold">
                                  {item.nameOrEmail}
                                </Text>
                              ),
                            }}
                          />
                        ) : (
                          <T keyName="c.Invite_a_new_user_to_account" />
                        )}
                      </Text>
                    </HStack>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>
    );
  },
);
